<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            color="primary"
            x-small
            text
            @click="() => $router.push({ name: 'series' })"
            :disabled="submittingForm"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Ano
        </h1>
      </v-col>
    </v-row>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submitForm)" :disabled="submittingForm">
        <v-row>
          <v-col cols="12">
            <v-card class="pa-4 mb-4" elevation="1">
              <v-card-title class="text-subtitle-1 font-weight-bold pa-0 mb-4">
                Informações Básicas:
              </v-card-title>

              <v-row>
                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Descrição" rules="required" v-slot="{ errors }">
                    <e-label class="required">Descrição</e-label>
                    <v-text-field
                      id="input-descricao"
                      :error-messages="errors"
                      v-model="form.descricao"
                      placeholder="Digite a descrição do ano"
                      dense
                      outlined
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Curso" rules="required" v-slot="{ errors }">
                    <e-label class="required">Curso</e-label>
                    <e-autocomplete
                      id="autocomplete-curso"
                      :items="cursosList"
                      :loading="carregandoCursos"
                      :item-text="(curso) => curso.descricao"
                      :item-value="(curso) => curso.id"
                      :error-messages="errors"
                      v-model="form.curso_id"
                      placeholder="Selecione um curso"
                      dense
                      outlined
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="historico" v-slot="{ errors }">
                    <div class="d-flex align-center mt-6">
                      <v-checkbox
                        id="checkbox-historico"
                        label="Histórico?"
                        v-model="form.historico"
                        dense
                        hide-details
                        class="mt-0"
                      ></v-checkbox>
                      <v-tooltip top max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            id="icon-info-historico"
                            v-bind="attrs"
                            v-on="on"
                            color="grey"
                            size="20"
                            class="ml-1"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span class="text-caption">
                          Marque esta opção para incluir esta série nas opções de série ao criar o
                          histórico escolar do aluno.
                        </span>
                      </v-tooltip>
                    </div>
                    <div v-if="errors.length" class="v-messages error--text text-caption mt-1">
                      {{ errors[0] }}
                    </div>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="pa-4" elevation="1">
              <v-card-title
                class="text-subtitle-1 font-weight-bold pa-0 mb-4 d-flex justify-space-between align-center"
              >
                <div class="d-flex align-center">
                  Disciplinas:
                  <v-chip color="primary" class="ml-2">
                    {{ form.disciplinaIdList.length }} selecionadas
                  </v-chip>
                </div>
                <v-checkbox
                  class="mr-5"
                  id="checkbox-marcar-todos"
                  :label="marcarTodosLabel"
                  v-model="marcarTodas"
                  @change="marcarTodasDisciplinas"
                ></v-checkbox>
              </v-card-title>

              <e-label class="mb-3">Selecione as disciplinas e defina a carga horária:</e-label>

              <v-row>
                <v-col
                  v-for="(disciplina, index) in disciplinasList"
                  :key="disciplina.id"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <v-card
                    class="pa-3 mb-3"
                    :class="{
                      'selected-disciplina': form.disciplinaIdList.includes(disciplina.id),
                    }"
                    elevation="1"
                    outlined
                  >
                    <div class="d-flex align-start">
                      <v-checkbox
                        :id="`checkbox-disciplina-${disciplina.id}`"
                        :label="disciplina.descricao"
                        :value="disciplina.id"
                        v-model="form.disciplinaIdList"
                        :data-testid="`disciplina-checkbox-${disciplina.id}`"
                        hide-details
                        class="mt-0 flex-grow-1"
                      ></v-checkbox>
                    </div>

                    <v-expand-transition>
                      <div v-if="form.disciplinaIdList.includes(disciplina.id)" class="mt-3">
                        <ValidationProvider
                          :name="`Carga horária - ${disciplina.descricao}`"
                          rules="required|min_value:1|max_value:999"
                          v-slot="{ errors }"
                        >
                          <e-label
                            class="required text-caption"
                            v-if="!disciplina.disciplina_extra_curricular"
                            >Carga horária (horas)
                          </e-label>
                          <v-text-field
                            v-if="!disciplina.disciplina_extra_curricular"
                            type="number"
                            v-model.number="disciplina.carga_horaria_disciplina"
                            :error-messages="errors"
                            :id="`campo-carga-horaria-${index}`"
                            placeholder="Ex: 180"
                            min="1"
                            max="999"
                            v-mask="'###'"
                            dense
                            outlined
                            hide-details="auto"
                            class="carga-horaria-field"
                          />

                          <!-- Container para switch e tooltip alinhados horizontalmente -->
                          <div class="d-flex align-center mt-2">
                            <v-switch
                              :id="`input-inep-status-${disciplina.id}`"
                              v-model="disciplina.disciplina_extra_curricular"
                              label="Complementar"
                              hide-details
                              @change="limparCargaHoraria(disciplina)"
                            />
                            <v-tooltip right max-width="300">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  id="icon-info-historico"
                                  v-bind="attrs"
                                  v-on="on"
                                  color="grey"
                                  size="20"
                                  class="ml-4 mt-5"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span class="text-caption">
                                Ao ativar a opção 'Complementar' a carga horária da disciplina
                                complementar será zero e a disciplina não aparecerá nos relatórios e
                                dashboards.
                              </span>
                            </v-tooltip>
                          </div>
                        </ValidationProvider>
                      </div>
                    </v-expand-transition>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Mensagem quando não há disciplinas -->
              <div v-if="disciplinasList.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-book-off</v-icon>
                <div class="text-body-1 grey--text">Nenhuma disciplina disponível</div>
              </div>
            </v-card>
          </v-col>

          <!-- Botões de Ação -->
          <v-col cols="12" class="mt-6">
            <v-card class="pa-4" elevation="0" color="grey lighten-4">
              <v-row>
                <v-col cols="12" md="6" class="d-flex">
                  <v-btn
                    id="btn-limpar-dados"
                    color="primary"
                    :disabled="submittingForm"
                    @click="limparDados"
                    block
                    outlined
                    class="mr-md-2"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Limpar Dados
                  </v-btn>
                  <v-btn
                    id="btn-enviar-dados"
                    color="primary"
                    :disabled="submittingForm"
                    :loading="submittingForm"
                    type="submit"
                    block
                    class="ml-md-2"
                  >
                    <v-icon left>mdi-check</v-icon>
                    {{ editing ? "Atualizar" : "Criar" }} Ano
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";

export default {
  components: { ELabel },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("Cursos", ["carregandoCursos"]),
    ...mapGetters("Series", ["series"]),
    marcarTodosLabel() {
      return this.marcarTodas ? "Desmarcar todos" : "Marcar todos";
    },
  },
  async mounted() {
    await this.loadData();
  },
  watch: {
    "form.disciplinaIdList": {
      handler(newValue) {
        this.marcarTodas = newValue.length === this.disciplinasList.length;
      },
      deep: true,
    },
  },
  data() {
    return {
      submittingForm: false,
      form: {
        disciplinaIdList: [],
        id: null,
      },
      marcarTodas: false,
      cursosList: [],
      disciplinasList: [],
    };
  },
  methods: {
    ...mapActions("Cursos", ["loadCursos"]),
    ...mapActions("Series", ["loadSeries"]),
    ...mapActions("Disciplinas", ["loadDisciplinas"]),
    limparCargaHoraria(disciplina) {
      if (disciplina.disciplina_extra_curricular) {
        disciplina.carga_horaria_disciplina = null;
      }
    },
    marcarTodasDisciplinas() {
      if (this.marcarTodas) {
        this.form.disciplinaIdList = this.disciplinasList.map((d) => d.id);
      } else {
        this.form.disciplinaIdList = [];
      }
    },
    limparDados() {
      this.form.disciplinaIdList = [];
      this.disciplinasList.forEach((disciplina) => {
        disciplina.carga_horaria_disciplina = null;
        disciplina.disciplina_extra_curricular = false;
      });
      this.marcarTodas = false;
    },
    deixarLetraMinuscula(value, campo) {
      const novo_valor = value
        .replace(/\s{2,}/g, " ")
        .trim()
        .toLowerCase()
        .split(" ");
      const valor = novo_valor
        .map((word) => word.charAt(0).toLowerCase() + word.slice(1))
        .join(" ");
      this.form[campo] = valor;
    },
    async submitForm() {
      this.submittingForm = true;

      const disciplinasPayload = {};
      this.form.disciplinaIdList.forEach((id) => {
        const disciplina = this.disciplinasList.find((d) => d.id === id);
        if (disciplina) {
          disciplinasPayload[id] = {
            carga_horaria_disciplina: disciplina.carga_horaria_disciplina || null,
            disciplina_extra_curricular: disciplina.disciplina_extra_curricular || false,
          };
        }
      });

      const payload = {
        ...this.form,
        disciplinaIdList: disciplinasPayload,
      };

      try {
        if (this.editing) {
          await this.$services.seriesService.atualizarSerie(payload);
          this.$toast.success("Série atualizada com sucesso");
        } else {
          await this.$services.seriesService.criarSerie(payload);
          this.$toast.success("Série criada com sucesso");
        }
        this.$router.push({ name: "series" });
      } catch (error) {
        this.$toast.danger(error.message);
      }
      this.submittingForm = false;
    },
    async loadData() {
      try {
        this.$loaderService.open("Carregando dados");

        const { serie_id } = this.$route.params;
        const serie = serie_id ? await this.$services.seriesService.getSerie(serie_id) : {};

        const response = await this.$services.cursosService.syncAll();
        this.cursosList = response.map((curso) => ({
          ...curso,
          "data-testid": `curso-item-${curso.id}`,
        }));

        const allDisciplinas = await this.$services.disciplinasService.syncAll();
        this.disciplinasList = allDisciplinas.map((disciplina) => {
          const associatedDisciplina = serie?.disciplinas?.find((d) => d.id === disciplina.id);
          return {
            ...disciplina,
            "data-testid": `disciplina-item-${disciplina.id}`,
            carga_horaria_disciplina: associatedDisciplina
              ? associatedDisciplina.pivot.carga_horaria_disciplina
              : null,
            disciplina_extra_curricular: associatedDisciplina
              ? !!associatedDisciplina.pivot.disciplina_extra_curricular
              : false,
          };
        });

        this.form = {
          ...serie,
          disciplinaIdList: serie?.disciplinas?.map((disciplina) => disciplina.id) || [],
          id: serie?.id || null,
        };

        console.log(this.disciplinasList);

        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
      }
    },
  },
};
</script>

<style scoped>
.required::after {
  content: " *";
  color: red;
}

.selected-disciplina {
  border: 2px solid #2196f3;
  background-color: #e3f2fd;
}

.carga-horaria-field {
  max-width: 120px;
}

/* Melhorias de espaçamento */
.v-card {
  border-radius: 8px;
}

/* Transição suave para os cards */
.v-card {
  transition: all 0.3s ease;
}
</style>
